import { getLabel, getValueByPath } from '@apia/util';
import { TObjectInspection, ValidationsMap } from './types';
import { makeObservable, observable } from 'mobx';
import { ReactNode } from 'react';
import { ValidableContext } from './context';
import { setValueByPath } from './util';

export async function validate<T extends object>(
  target: T,
  validations: ValidationsMap<T>,
): Promise<true | Record<string, string>> {
  const errors: Record<string, string> = {};
  let focus: undefined | (() => void) = undefined;

  for (const key in validations) {
    const validation = validations[key as keyof ValidationsMap<T>]!;

    const value = getValueByPath(target as Record<string, unknown>, key);

    if (validation.validationFunction) {
      const validationResult = await validation.validationFunction(value);
      if (validationResult !== true) {
        errors[key] = validationResult;
      }
    } else if (validation.required && !value) {
      errors[key] =
        validation.errorsLabels?.required ??
        getLabel('msgReqField', { text: { TOK1: key } }).text;
    } else if (
      validation.minLength !== undefined &&
      value &&
      String(value).length < validation.minLength
    ) {
      errors[key] =
        validation.errorsLabels?.minLength ??
        getLabel('msgMinLength', { text: { TOK1: key } }).text;
    } else if (
      validation.maxLength !== undefined &&
      value &&
      String(value).length > validation.maxLength
    ) {
      errors[key] =
        validation.errorsLabels?.maxLength ??
        getLabel('msgMaxLength', { text: { TOK1: key } }).text;
    } else if (
      validation.min !== undefined &&
      value &&
      Number(value) < validation.min
    ) {
      errors[key] =
        validation.errorsLabels?.min ??
        getLabel('msgMinValue', { text: { TOK1: key } }).text;
    } else if (
      validation.max !== undefined &&
      value &&
      Number(value) > validation.max
    ) {
      errors[key] =
        validation.errorsLabels?.max ??
        getLabel('msgMaxValue', { text: { TOK1: key } }).text;
    } else if (
      validation.pattern &&
      value &&
      !new RegExp(validation.pattern).test(String(value))
    ) {
      errors[key] =
        validation.errorsLabels?.pattern ??
        getLabel('msgExpRegFal', { text: { TOK1: key } }).text;
    }

    if (errors[key] && !focus) {
      focus =
        validation.focus ||
        (() =>
          document
            .querySelector<HTMLElement>(`[name=${key.replace(/\./g, '\\.')}]`)
            ?.focus());
    }
  }

  const hasErrors = Object.keys(errors).length > 0;
  hasErrors && focus?.();

  return hasErrors ? errors : true;
}

export abstract class Validable<T extends object> {
  errors: Record<string, string> = {};

  public abstract state: T;

  public abstract validations: ValidationsMap<T>;

  constructor() {
    makeObservable(this, {
      errors: observable,
    });
  }

  public Context = ({ children }: { children: ReactNode }) => {
    return (
      <ValidableContext.Provider value={this}>
        {children}
      </ValidableContext.Provider>
    );
  };

  public getError(path: TObjectInspection<T>) {
    return this.errors[path];
  }

  public getValidations(path: TObjectInspection<T>) {
    return this.validations[path];
  }

  public getValue<V>(path: TObjectInspection<T>) {
    return getValueByPath<V>(this.state, path)!;
  }

  public setValue<V>(path: TObjectInspection<T>, value: V) {
    setValueByPath(this.state, path, value);

    if (this.errors[path]) {
      const errors = { ...this.errors };
      delete errors[path];
      this.errors = errors;
    }
  }

  async validate(): Promise<true | Record<string, string>> {
    const result = await validate<T>(this.state, this.validations);

    this.errors = result === true ? {} : result;

    return result;
  }
}

export * from './types';
export * from './context';
export * from './fields';
